/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import Page from 'components/page/Page';

import CcmCampaignDetails from 'modules/campaigns/components/ccm/components/details/CcmCampaignDetails';
import CcmCampaignInfoNote from 'modules/campaigns/components/ccm/components/info-note/CcmCampaignInfoNote';

import {
  fetchCampaignBrief,
  resetCampaignBrief
} from 'modules/campaigns/reducers/campaignBrief';
import {
  fetchCampaignOverview,
  resetCampaignOverview
} from 'modules/campaigns/reducers/campaignOverview';
import {
  searchOrganization,
  selectOrganizationById
} from 'modules/organizations/reducers/organizations';

import { navigate } from 'libs/router';

import { isCcmModerator } from 'utils/account';
import {
  getCcmCampaignSubnavigation,
  getCcmCreatorlessCampaignSubnavigation
} from 'utils/navigation';
import { trackCampaignSwitch } from 'modules/tracking/utils/tracking';

import type {
  Account,
  Campaign,
  CampaignPageTypeCcm,
  CampaignType,
  Organization,
  OrganizationSearch,
  Params,
  Role
} from 'types';

type OwnProps = {
  documentTitle: string,
  params: Params,
  campaignType: CampaignType,
  pageType: CampaignPageTypeCcm,
  children: React.ReactNode,
  childrenClassName?: string
};

type StateProps = {
  account: Account,
  roles: Array<Role>,
  organization: Organization,
  campaignId: number,
  campaign: Campaign,
  isCcmCreatorlessEnabled: boolean
};

type DispatchProps = {
  dispatchFetchCampaign: (campaignId: number) => Promise<any>,
  dispatchFetchCampaignOverview: (campaignId: number, t?: CampaignType) => Promise<void>,
  dispatchSearchOrganization: (campaignId: number) => Promise<OrganizationSearch>,
  dispatchSelectOrganizationById: (organizationId: number) => Promise<any>,
  dispatchResetCampaign: () => Promise<any>,
  dispatchResetCampaignOverview: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const BaseCcmCampaignPage: React.FC<Props> = ({
  documentTitle,
  pageType,
  children,
  childrenClassName,
  account,
  roles,
  organization,
  campaignId,
  campaign,
  isCcmCreatorlessEnabled,
  dispatchFetchCampaign,
  dispatchFetchCampaignOverview,
  dispatchSearchOrganization,
  dispatchSelectOrganizationById,
  dispatchResetCampaign,
  dispatchResetCampaignOverview
}): JSX.Element => {
  const fetchCampaign = (): void => {
    dispatchFetchCampaign(campaignId)
      .then(() => dispatchSearchOrganization(campaignId))
      .then(org => org?.id &&
        dispatchSelectOrganizationById(org.id)
      );
  };

  React.useEffect(() => {
    trackCampaignSwitch(account, organization, campaignId);
  }, [account, organization, campaignId]);

  React.useEffect(() => {
    fetchCampaign();

    return () => {
      dispatchResetCampaign();
    };
  }, []);

  React.useEffect(() => {
    if (campaign?.campaignType) {
      dispatchFetchCampaignOverview(campaignId, campaign.campaignType);
    }

    return () => {
      dispatchResetCampaignOverview();
    };
  }, [campaign?.campaignType]);

  const childrenClasses = classnames(childrenClassName);
  const isModerator = isCcmModerator(roles);

  React.useEffect(() => {
    if (campaign.campaignType === 'ccm_creatorless' && !isCcmCreatorlessEnabled) {
      navigate.to('/');
    }
  }, [campaign.campaignType, isCcmCreatorlessEnabled]);

  const subnavigation = (() => {
    if (Boolean(campaign.campaignType) === false) {
      return [];
    }

    return campaign.campaignType === 'ccm_creatorless'
      ? getCcmCreatorlessCampaignSubnavigation(campaignId)
      : getCcmCampaignSubnavigation(campaignId, isModerator);
  })();

  return (
    <div className="base-ccm-campaign-page">
      <Page
        documentTitle={documentTitle}
        infoNote={(
          <CcmCampaignInfoNote
            campaign={campaign}
            isModerator={isModerator}
          />
        )}
        topBlock={pageType !== 'outfits' && (
          <CcmCampaignDetails
            campaign={campaign}
            hideSpecifics={pageType === 'payments' || pageType === 'report'}
          />
        )}
        subnavigation={subnavigation}
      >
        <div className={childrenClasses}>
          {children}
        </div>
      </Page>
    </div>
  );
};

const mapStateToProps = ({
  account,
  organizations,
  campaignBrief,
  runtimeConfig
}, { params }): StateProps => ({
  account: account.currentAccount,
  roles: account.roles,
  organization: organizations.currentOrganization,
  campaignId: Number(params.id),
  campaign: campaignBrief.brief,
  isCcmCreatorlessEnabled: Boolean(runtimeConfig.config.isCcmEnabled)
    && Boolean(runtimeConfig.config.isCcmCreatorlessEnabled)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCampaign: (campaignId: number) => (
    dispatch(fetchCampaignBrief(campaignId))
  ),
  dispatchFetchCampaignOverview: (campaignId, campaignType) => (
    dispatch(fetchCampaignOverview(campaignId, campaignType))
  ),
  dispatchSearchOrganization: (campaignId: number) => (
    dispatch(searchOrganization('campaign', campaignId))
  ),
  dispatchSelectOrganizationById: (organizationId: number) => (
    dispatch(selectOrganizationById(organizationId))
  ),
  dispatchResetCampaign: () => (
    dispatch(resetCampaignBrief())
  ),
  dispatchResetCampaignOverview: () => (
    dispatch(resetCampaignOverview())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(BaseCcmCampaignPage);
